import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GridView from "src/Components/GridView/GridView";
import SmallDialog from "src/Components/Dialog/SmallDialog";
import MoreOptionsMenu from "src/Components/MoreOptions/MoreOptions";
import { InfoMessageType } from "src/Components/InfoBar/InfoBar";
import { IClinicInfo } from "src/Utils/Hooks/GetClinicInfo/useGetClinicInfo";
import { useClinicLogo } from "src/Utils/Hooks/ClinicLogo/useClinicLogo";
import { useClinicContext } from "src/Utils/Hooks/ClinicContext";
import { InfoIcon, UploadIcon } from "src/SharedComponents/Icons/Icons";
import { logger } from "src/Utils/LoggerService/LoggerService";
import LogoPreviewDialog from "./LogoPreviewDialog";
import './ClinicLogo.css';

interface IClinicLogoParams {
    clinicDetails: IClinicInfo;
    updatePageLoader: (load: boolean) => void;
    setInfo: (message: string, type: InfoMessageType) => void;
}

const MAX_LOGO_FILE_SIZE_BYTES = 5 * 1024 * 1024;

// Formats the clinic logo may be uploaded in. The extensions are surfaced to
// the OS file picker via the input's `accept` attribute, so the dialog filters
// to these types before the user can pick anything else.
const ALLOWED_LOGO_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.svg', '.webp'];
const LOGO_FILE_ACCEPT = [
    ...ALLOWED_LOGO_EXTENSIONS,
    'image/png', 'image/jpeg', 'image/svg+xml', 'image/webp',
].join(',');

const hasAllowedExtension = (fileName: string) =>
    ALLOWED_LOGO_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));

// The API returns the logo as a bare base64 string with no data-URI scheme,
// unlike the blob: URLs used for the local optimistic preview.
const toDisplayableSrc = (logo?: string) => {
    if (!logo) {
        return undefined;
    }
    return /^(data:|blob:|https?:)/.test(logo) ? logo : `data:image/png;base64,${logo}`;
};

const ClinicLogo: React.FC<IClinicLogoParams> = ({ clinicDetails, updatePageLoader, setInfo }) => {
    const { t } = useTranslation();
    const { currentUser } = useClinicContext();
    const {
        uploadClinicLogo, uploadResponse, uploadLoading, uploadError,
        deleteClinicLogo, deleteResponse, deleteLoading, deleteError
    } = useClinicLogo();
    const [logoPreview, setLogoPreview] = useState<string | undefined>(toDisplayableSrc(clinicDetails.logo));
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showMaxSizeError, setShowMaxSizeError] = useState(false);
    const [pendingLogo, setPendingLogo] = useState<File | undefined>(undefined);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const localPreviewUrlRef = useRef<string | null>(null);

    const revokeLocalPreview = useCallback(() => {
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
            localPreviewUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        revokeLocalPreview();
        setLogoPreview(toDisplayableSrc(clinicDetails.logo));
    }, [clinicDetails.logo, revokeLocalPreview]);

    useEffect(() => revokeLocalPreview, [revokeLocalPreview]);

    useEffect(() => {
        if (uploadResponse) {
            logger.logInformation(`Updated clinic logo successfully, clinicId is ${clinicDetails.clinicId}.`);
            setInfo(t('ClinicLogoUpdatedSuccessMsg'), InfoMessageType.success);
        }
    }, [uploadResponse, setInfo, t, clinicDetails.clinicId]);

    useEffect(() => {
        if (uploadError) {
            logger.logError(`Error occured while updating the clinic logo for clinic id: ${clinicDetails.clinicId}.`);
            setInfo(uploadError, InfoMessageType.error);
            revokeLocalPreview();
            setLogoPreview(toDisplayableSrc(clinicDetails.logo));
        }
    }, [uploadError, setInfo, clinicDetails.clinicId, clinicDetails.logo, revokeLocalPreview]);

    useEffect(() => {
        if (deleteResponse) {
            logger.logInformation(`Deleted clinic logo successfully, clinicId is ${clinicDetails.clinicId}.`);
            setInfo(t('ClinicLogoDeletedSuccessMsg'), InfoMessageType.success);
            setLogoPreview(undefined);
        }
    }, [deleteResponse, setInfo, t, clinicDetails.clinicId]);

    useEffect(() => {
        if (deleteError) {
            logger.logError(`Error occured while deleting the clinic logo for clinic id: ${clinicDetails.clinicId}.`);
            setInfo(deleteError, InfoMessageType.error);
        }
    }, [deleteError, setInfo, clinicDetails.clinicId]);

    useEffect(() => {
        updatePageLoader(uploadLoading || deleteLoading);
    }, [uploadLoading, deleteLoading, updatePageLoader]);

    const onLogoSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        if (!file.type.startsWith('image/') || !hasAllowedExtension(file.name)) {
            setShowMaxSizeError(false);
            setInfo(t('InvalidLogoFileType'), InfoMessageType.error);
            return;
        }
        // The 5MB ceiling is a property of the file, so it applies whether the
        // clinic is adding its first logo or replacing an existing one.
        if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
            setPendingLogo(undefined);
            if (logoPreview) {
                // The inline hint lives under the Add Logo button, which isn't
                // on screen once a logo exists, so say it in the info bar.
                setInfo(t('MaxLogoFileSize'), InfoMessageType.error);
            } else {
                setShowMaxSizeError(true);
            }
            return;
        }
        setShowMaxSizeError(false);
        // Let the user frame the logo, and see how it will land in PDF exports
        // and screenshots, before anything is uploaded.
        setPendingLogo(file);
    }, [setInfo, t, logoPreview]);

    const onLogoConfirmed = useCallback((logo: File) => {
        setPendingLogo(undefined);
        revokeLocalPreview();
        localPreviewUrlRef.current = URL.createObjectURL(logo);
        setLogoPreview(localPreviewUrlRef.current);
        uploadClinicLogo(logo, clinicDetails.clinicId);
    }, [uploadClinicLogo, revokeLocalPreview, clinicDetails.clinicId]);

    // The bin inside the dialog. Nothing has been uploaded yet, so this
    // discards the pick; if the clinic already has a saved logo it falls
    // through to the usual delete confirmation.
    const onPendingLogoDiscarded = useCallback(() => {
        setPendingLogo(undefined);
        if (clinicDetails.logo) {
            setShowDeleteConfirm(true);
        }
    }, [clinicDetails.logo]);

    const confirmDeleteLogo = useCallback(() => {
        setShowDeleteConfirm(false);
        deleteClinicLogo(clinicDetails.clinicId);
    }, [deleteClinicLogo, clinicDetails.clinicId]);

    return (
        <GridView
            title={t('ClinicLogo')}
            headerNode={
                <div className="col-4 d-flex justify-content-end">
                    <button
                        type="button"
                        className="clinic-logo-info-icon"
                        data-tooltip={t('ClinicLogoInfoTooltip')}
                        data-flow="bottom"
                        data-testid="clinic-logo-info-icon">
                        <InfoIcon />
                    </button>
                </div>
            }>
            {showDeleteConfirm &&
                <SmallDialog
                    submitButtonText={t('DeleteLogo')}
                    title={t('DeletingLogo')}
                    submitButtonColor="var(--color-error)"
                    cancelButtonColor="black"
                    onClose={() => setShowDeleteConfirm(false)}
                    onSubmit={confirmDeleteLogo}>
                    <div className="mt-2" style={{ textAlign: 'center' }}>
                        {t('DeleteLogoInstruction')}
                    </div>
                </SmallDialog>
            }
            <div className="clinic-logo-content">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={LOGO_FILE_ACCEPT}
                    className="clinic-logo-file-input"
                    onChange={onLogoSelected}
                    data-testid="clinic-logo-file-input" />
                {pendingLogo &&
                    <LogoPreviewDialog
                        file={pendingLogo}
                        onSave={onLogoConfirmed}
                        onClose={() => setPendingLogo(undefined)}
                        onReplace={() => fileInputRef.current?.click()}
                        onDelete={onPendingLogoDiscarded} />
                }
                {logoPreview ?
                    <div className="clinic-logo-preview-row">
                        <div className="clinic-logo-image-frame">
                            <img
                                src={logoPreview}
                                alt={t('ClinicLogo')}
                                className="clinic-logo-preview"
                                data-testid="clinic-logo-preview" />
                        </div>
                        <MoreOptionsMenu id="three-dots-clinic-logo" disabled={!currentUser?.isClinicAdmin}>
                            <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>
                                    <div
                                        className="dropdown-item"
                                        id="upload-new-logo"
                                        onClick={() => fileInputRef.current?.click()}>
                                        {t('UploadNewLogo')}
                                    </div>
                                </li>
                                <li>
                                    <div
                                        className="dropdown-item"
                                        id="delete-logo"
                                        onClick={() => setShowDeleteConfirm(true)}>
                                        {t('DeleteLogo')}
                                    </div>
                                </li>
                            </ul>
                        </MoreOptionsMenu>
                    </div> :
                    <>
                        <button
                            type="button"
                            className="add-logo-button"
                            disabled={uploadLoading || deleteLoading || !currentUser?.isClinicAdmin}
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="add-logo-button">
                            <UploadIcon />
                            <span>{t('AddLogo')}</span>
                        </button>
                        <span
                            className={`add-logo-max-size${showMaxSizeError ? ' add-logo-max-size--error' : ''}`}
                            data-testid={showMaxSizeError ? 'add-logo-max-size-error' : 'add-logo-max-size'}>
                            {t('MaxLogoFileSize')}
                        </span>
                    </>
                }
            </div>
        </GridView>
    );
}
export default ClinicLogo;
